import { useTranslation } from 'react-i18next';
import type { WhatsAppPendingWaba } from '@/Domain/api/WhatsAppPhoneNumbers/WhatsAppPhoneNumbers.api';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';

interface PendingWabasPanelProps {
  wabas: WhatsAppPendingWaba[];
  onConfirm: (whatsappId: string) => void;
  onCancel: () => void;
  onReconnect: () => void;
  confirmingWabaId: string | null;
  isCancelling: boolean;
  isExpired: boolean;
  reconnectDisabled: boolean;
  cancelDisabled: boolean;
  errorMessage?: string | null;
}

export const PendingWabasPanel = ({
  wabas,
  onConfirm,
  onCancel,
  onReconnect,
  confirmingWabaId,
  isCancelling,
  isExpired,
  reconnectDisabled,
  cancelDisabled,
  errorMessage,
}: PendingWabasPanelProps) => {
  const { t } = useTranslation(['integrations']);
  const isBusy = confirmingWabaId !== null || isCancelling;

  return (
    <Alert
      variant="info"
      role="status"
      aria-live="polite"
      data-testid="whatsapp-pending-wabas-panel"
    >
      <AlertTitle>
        {t(
          isExpired
            ? 'whatsapp_phone_numbers.toast.staged_token_expired.title'
            : 'whatsapp_phone_numbers.select_account.title',
        )}
      </AlertTitle>
      <AlertDescription>
        {t(
          isExpired
            ? 'whatsapp_phone_numbers.toast.staged_token_expired.description'
            : 'whatsapp_phone_numbers.select_account.description',
        )}
      </AlertDescription>
      {!isExpired && (
        <div className="flex flex-col gap-2 pt-3.5 !pl-0">
          {wabas.map((waba) => {
            const numbers = waba.phone_numbers
              .map((number) => number.display_phone_number)
              .filter(Boolean)
              .join(', ');
            const accountLabel = waba.account_name
              ? `${waba.account_name} (${waba.whatsapp_id})`
              : waba.whatsapp_id;
            return (
              <div
                key={waba.whatsapp_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2"
                data-testid={`whatsapp-waba-option-${waba.whatsapp_id}`}
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-foreground text-sm">
                    {waba.account_name || waba.whatsapp_id}
                  </span>
                  <span className="truncate text-muted-foreground text-xs">
                    {numbers || t('whatsapp_phone_numbers.select_account.no_numbers')}
                  </span>
                </div>
                <Button
                  size="sm"
                  aria-label={`${t('whatsapp_phone_numbers.select_account.confirm')}: ${accountLabel}`}
                  onClick={() => onConfirm(waba.whatsapp_id)}
                  disabled={isBusy}
                  loading={confirmingWabaId === waba.whatsapp_id}
                  data-testid={`whatsapp-waba-confirm-${waba.whatsapp_id}`}
                >
                  {t('whatsapp_phone_numbers.select_account.confirm')}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      {errorMessage ? (
        <p
          className="pt-2.5 !pl-0 text-destructive text-xs"
          data-testid="whatsapp-waba-select-error"
        >
          {errorMessage}
        </p>
      ) : null}
      <div className="flex gap-2 pt-2.5 !pl-0">
        {isExpired && (
          <Button
            size="sm"
            variant="secondary"
            onClick={onReconnect}
            disabled={isBusy || reconnectDisabled}
            data-testid="whatsapp-waba-reconnect-button"
          >
            {t('integrations:reconnect')}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={isBusy || cancelDisabled}
          loading={isCancelling}
          data-testid="whatsapp-waba-cancel-button"
        >
          {t('whatsapp_phone_numbers.select_account.cancel')}
        </Button>
      </div>
    </Alert>
  );
};
